// ============================================================================
// quote-jobs/quote-number.ts — the Quote # for revisions and imported quotes
// (docs/job/GOAL.md A5, A7).
// ----------------------------------------------------------------------------
// Why this module exists:
//   - A5. A revision keeps its Quote # and adds "Rev N". Every surface that
//     shows a Quote # (Stage 5 header, PDF file name, email subject, Run Sheet,
//     All Quotes, search) must print it the same way, so they all go through
//     formatQuoteNumberLabel / formatQuoteNumberFileSegment.
//   - A7. An imported quote keeps its original number. An RJ Bird number
//     becomes qm_quote_no + quote_rev. Another builder's number becomes
//     source_quote_ref and no RBB number is allocated for it.
//     parseSourceQuoteNumber makes that call from the printed text.
//
// Rules it keeps:
//   - qm_quote_no is stored as the bare six digits YYMMXX. The tenant prefix
//     ("RBB") is display only and comes from formatQmQuoteNoDisplay, so this
//     module never decides which prefix to print.
//   - quote_rev is an integer 0-99 (the column constraint). Anything else is
//     treated as 0 on display and as "no revision" when parsing.
//   - The legacy '-R1' helpers in src/utils/quote-revision.ts are untouched.
//     They serve the older restart-from-source flow, not this one.
//   - Imported text comes from Word, PDF and RTF. Word's AutoFormat turns
//     ' - ' into an en dash and PDF text carries other dash forms, so every
//     place that accepts '-' as a separator accepts those too. Invisible
//     characters (zero-width space, soft hyphen, BOM) are removed before
//     parsing, so 'RBB<ZWSP>260605' reads as RBB260605.
//   - Pure functions. No network, no storage, no dates.
// ============================================================================

import { formatQmQuoteNoDisplay } from '../qm-quote-no';

export interface QuoteNumberInput {
    qmQuoteNo?: string | null;
    quoteRev?: number | null;
    sourceQuoteRef?: string | null;
}

export type ParsedSourceQuoteNumber =
    | { kind: 'tenant'; qmQuoteNo: string; quoteRev: number }
    | { kind: 'external'; sourceQuoteRef: string; quoteRev: number; sameIssuerPrefix: boolean }
    | { kind: 'none' };

export interface ParseSourceQuoteNumberOptions {
    /** The tenant's own Quote # prefixes, e.g. ['RBB']. Matched case-insensitively. */
    tenantPrefixes: string[];
    /**
     * The revision marker the extraction read separately ('Rev 2'), if any.
     * A bare whole number ('2') is accepted here, unlike in parseRevisionLabel,
     * because the caller has already said the value is the revision.
     */
    revisionLabel?: string | null;
}

/** quote_rev column range (GOAL A3). */
const MAX_QUOTE_REV = 99;
/** Longest external reference we store in source_quote_ref, and longest file segment. */
const MAX_REF_CHARS = 64;
/**
 * Input is cut to this length before any pattern runs. A real quote number is
 * a few dozen characters, so the cap only matters when a caller passes a whole
 * line or page by mistake, and it bounds the work done on that input.
 */
const MAX_PARSE_CHARS = 256;

/**
 * Characters read as '-' wherever a dash separator is allowed, for use inside
 * a character class: '-', U+2010 hyphen, U+2011 non-breaking hyphen, U+2012
 * figure dash, U+2013 en dash, U+2014 em dash, U+2015 horizontal bar, U+2212
 * minus, and the small and full-width hyphen forms.
 */
const DASHES = '\\-\\u2010-\\u2015\\u2212\\uFE58\\uFE63\\uFF0D';

/** Invisible characters PDF and DOCX text can carry inside a number. */
const INVISIBLE_RE = /[\u00AD\u200B-\u200D\u2060\uFEFF]/g;

// 'Rev', 'Revision', 'Revised', 'Ver', 'Version', 'v', 'R'. Longer words come
// first so 'Revised' is never read as 'Rev' + 'ised'.
const REVISION_KEYWORD = '(?:rev(?:ision|ised)?|ver(?:sion)?|v|r)';
// Optional 'No.' / 'Number' between the keyword and the digits ('Revision No. 2').
const REVISION_NO_WORD = '(?:\\s*(?:no|number)\\.?)?';
// What may sit between the keyword and the digits: 'Rev 2', 'Rev: 2', 'Rev#2', 'Rev-2'.
const REVISION_GAP = `[\\s:#.${DASHES}]*`;
const REVISION_NUMBER = '(\\d+)(\\.\\d+)?';

/**
 * A whole label that is only a revision marker. Leading separators and
 * brackets are allowed so the tail of 'RBB260605-R2' or 'RBB260605 (Rev 1)'
 * can be read with the same pattern.
 */
const REVISION_LABEL_RE = new RegExp(
    `^[\\s${DASHES}_#/,(\\[]*${REVISION_KEYWORD}\\.?${REVISION_NO_WORD}${REVISION_GAP}${REVISION_NUMBER}\\.?\\s*[)\\]]*\\s*$`,
    'i',
);

/**
 * A revision marker at the end of an external reference. It needs a separator
 * or an opening bracket before it, so 'QR592460-R1' splits but a reference
 * like 'ABCR4' is left whole.
 */
const EXTERNAL_TAIL_RE = new RegExp(
    `^(.*?[^\\s${DASHES}_,/(\\[])((?:\\s*[(\\[]|[\\s${DASHES}_,/]+\\(?)\\s*${REVISION_KEYWORD}\\.?${REVISION_NO_WORD}${REVISION_GAP}\\d+(?:\\.\\d+)?\\.?\\s*[)\\]]?\\s*)$`,
    'i',
);

function escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeRev(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= MAX_QUOTE_REV
        ? value
        : 0;
}

function cleanPrefixes(prefixes: string[] | null | undefined): string[] {
    if (!Array.isArray(prefixes)) return [];
    const cleaned = prefixes
        .filter((p): p is string => typeof p === 'string')
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
    // Longest first, so 'RBB' is tried before a shorter 'RB'.
    return [...new Set(cleaned)].sort((a, b) => b.length - a.length);
}

/**
 * True when `sixDigits` is exactly six digits YYMMXX with month 01-12 and
 * counter 01-99. The year is not range-checked: every YY is a valid year.
 */
export function isValidTenantQuoteNo(sixDigits: string): boolean {
    if (typeof sixDigits !== 'string' || !/^\d{6}$/.test(sixDigits)) return false;
    const month = parseInt(sixDigits.slice(2, 4), 10);
    const counter = parseInt(sixDigits.slice(4, 6), 10);
    return month >= 1 && month <= 12 && counter >= 1;
}

/**
 * Reads a revision number from a printed marker: 'Rev 2', 'Rev2.0',
 * 'Revised 1.0', 'Revision 3', 'R4', '-R1', 'Version 2', 'v2', '(Rev 1)'.
 * Returns null for anything else, including:
 *   - letter revisions ('Rev A'), which quote_rev cannot hold;
 *   - a bare number ('2'), which cannot be told apart from a page or item number;
 *   - a fraction other than zeros ('Rev 1.5'), which is not a whole revision;
 *   - numbers above 99, the quote_rev column limit.
 */
export function parseRevisionLabel(label: string | null | undefined): number | null {
    if (typeof label !== 'string') return null;
    const text = label.trim();
    if (!text) return null;
    const match = text.match(REVISION_LABEL_RE);
    if (!match || match[1] === undefined) return null;
    const fraction = match[2];
    if (fraction !== undefined && !/^\.0+$/.test(fraction)) return null;
    const rev = parseInt(match[1], 10);
    if (!Number.isFinite(rev) || rev > MAX_QUOTE_REV) return null;
    return rev;
}

/**
 * options.revisionLabel is already known to be a revision marker, so a bare
 * whole number there ('2', or '2.0') is the revision. A DOCX header table
 * printed as "Revision | 2" yields exactly that, and the extraction turns a
 * numeric 2 into '2'. Everything else goes through parseRevisionLabel.
 */
function parseRevisionOption(label: string | null | undefined): number | null {
    if (typeof label !== 'string') return null;
    const bare = label.trim().match(/^(\d+)(?:\.0+)?$/);
    if (bare && bare[1] !== undefined) {
        const rev = parseInt(bare[1], 10);
        return rev <= MAX_QUOTE_REV ? rev : null;
    }
    return parseRevisionLabel(label);
}

/**
 * Decides whether a printed quote number is the tenant's own or another
 * builder's (GOAL A7).
 *
 * Tenant number: a tenant prefix (any case), then optionally a space, a dash
 * (any of DASHES), '/' or '#', then exactly six digits that pass
 * isValidTenantQuoteNo, then optionally a revision marker ('Rev 2', '-R2',
 * 'Revised 1.0', '(Rev 1)', 'Rev-2'). Sentence punctuation after it
 * ('RBB260605.' at the end of a line) is ignored. The result carries the bare
 * six digits, which is how qm_quote_no is stored.
 *
 * Six bare digits with no prefix are ALSO a tenant number, but only when the
 * tenant has at least one prefix configured and the digits validate. Bare
 * YYMMXX is the tenant's stored form, and older exports print it without the
 * prefix. With no prefix configured there is no evidence the tenant issues
 * YYMMXX numbers at all, so a six-digit number is not claimed. The cost of
 * this choice: another builder's number that happens to look like YYMMXX is
 * read as the tenant's. The import's duplicate check (A7 "offer that quote")
 * and the review screen are where that gets caught.
 *
 * Anything else non-empty is external: trimmed, inner whitespace collapsed,
 * cut to 64 characters. A revision marker at its end moves into quoteRev and
 * is removed from the reference, so the label helper prints it once
 * ('Q-123 Rev 2', not 'Q-123 Rev 2 Rev 2') and revisions of one external
 * quote share one reference. The marker is only removed when what remains
 * still holds a digit: in 'Ref: R12', 'Unit R2' or 'RBB-R1' the R-number is
 * the identifier, and removing it would store a label with no number. The
 * cost: a reference made only of letters keeps its marker ('ABC Rev 2' stays
 * whole with quoteRev 0). sameIssuerPrefix is true when the reference
 * starts with a tenant prefix that is not followed by a letter, e.g. the
 * legacy 'RBB / WRHH (0126)', but not 'RBBQ Constructions'.
 *
 * quoteRev comes from the marker in `raw`, else from options.revisionLabel,
 * else 0. A marker in `raw` wins even when it says 'Rev 0'.
 */
export function parseSourceQuoteNumber(
    raw: string | null | undefined,
    options: ParseSourceQuoteNumberOptions,
): ParsedSourceQuoteNumber {
    if (typeof raw !== 'string') return { kind: 'none' };
    const text = raw.replace(INVISIBLE_RE, '').replace(/\s+/g, ' ').trim().slice(0, MAX_PARSE_CHARS).trim();
    if (!text) return { kind: 'none' };

    const prefixes = cleanPrefixes(options?.tenantPrefixes);
    const labelRev = parseRevisionOption(options?.revisionLabel ?? null);

    if (prefixes.length > 0) {
        const alternation = prefixes.map(escapeRegExp).join('|');
        // The prefix group is optional: bare YYMMXX counts only because a prefix exists.
        const tenantRe = new RegExp(`^(?:(?:${alternation})\\s*[${DASHES}/#]?\\s*)?(\\d{6})(?!\\d)(.*)$`, 'i');
        const match = text.match(tenantRe);
        if (match && match[1] !== undefined && isValidTenantQuoteNo(match[1])) {
            // Trailing sentence punctuation belongs to the surrounding text, not the number.
            const rest = (match[2] ?? '').replace(/[\s.,;:]+$/, '').trim();
            if (!rest) {
                return { kind: 'tenant', qmQuoteNo: match[1], quoteRev: labelRev ?? 0 };
            }
            const markerRev = parseRevisionLabel(rest);
            if (markerRev !== null) {
                return { kind: 'tenant', qmQuoteNo: match[1], quoteRev: markerRev };
            }
            // Six digits followed by something that is not a revision: not a tenant number.
        }
    }

    let ref = text;
    let markerRev: number | null = null;
    const tail = text.match(EXTERNAL_TAIL_RE);
    if (tail && tail[1] !== undefined && tail[2] !== undefined) {
        const rev = parseRevisionLabel(tail[2]);
        const base = tail[1].trim();
        // Only strip when an identifier with a digit is left behind.
        if (rev !== null && /\d/.test(base)) {
            markerRev = rev;
            ref = base;
        }
    }
    ref = ref.slice(0, MAX_REF_CHARS).trim();

    const lowerRef = ref.toLowerCase();
    const sameIssuerPrefix = prefixes.some((prefix) => {
        if (!lowerRef.startsWith(prefix.toLowerCase())) return false;
        const next = ref.charAt(prefix.length);
        return !/[A-Za-z]/.test(next);
    });

    return {
        kind: 'external',
        sourceQuoteRef: ref,
        quoteRev: markerRev ?? labelRev ?? 0,
        sameIssuerPrefix,
    };
}

/**
 * The Quote # as people read it: 'RBB260605', 'RBB260605 Rev 1', an external
 * reference as stored ('Q-4471', 'Q-4471 Rev 2'), or '' when the quote has no
 * number of either kind. The tenant number wins when both exist.
 */
export function formatQuoteNumberLabel(input: QuoteNumberInput): string {
    const tenant = formatQmQuoteNoDisplay(input?.qmQuoteNo ?? null);
    const external = typeof input?.sourceQuoteRef === 'string' ? input.sourceQuoteRef.trim() : '';
    const base = tenant || external;
    if (!base) return '';
    const rev = normalizeRev(input?.quoteRev);
    return rev > 0 ? `${base} Rev ${rev}` : base;
}

/**
 * The Quote # for a file name: 'RBB260605', 'RBB260605-Rev1', or an external
 * reference with every character outside [A-Za-z0-9._-] replaced by '-'.
 * Runs of '-' collapse to one, leading and trailing '-' and '.' are removed
 * (a leading dot hides the file, a trailing dot is invalid on Windows), and
 * the whole segment, revision included, is at most 64 characters.
 */
export function formatQuoteNumberFileSegment(input: QuoteNumberInput): string {
    const tenant = formatQmQuoteNoDisplay(input?.qmQuoteNo ?? null);
    const external = typeof input?.sourceQuoteRef === 'string' ? input.sourceQuoteRef.trim() : '';
    const base = tenant || external;
    if (!base) return '';
    const rev = normalizeRev(input?.quoteRev);
    const suffix = rev > 0 ? `-Rev${rev}` : '';
    const safe = trimFileEdges(base.replace(/[^A-Za-z0-9._-]/g, '-').replace(/-+/g, '-'));
    if (!safe) return '';
    const cut = trimFileEdges(safe.slice(0, MAX_REF_CHARS - suffix.length));
    return cut ? `${cut}${suffix}` : '';
}

function trimFileEdges(text: string): string {
    return text.replace(/^[-.]+/, '').replace(/[-.]+$/, '');
}
